import { useCallback, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type {
  ImportFileEntry,
  ImportFileType,
  ImportStepDescriptor,
  ImportStepId,
  ImportWizardState,
  LayerTarget,
} from './types';
import { detectFileType, parseExcelFile, parseCsvFile } from './utils/fileParsers';
import { guessColumnMappingForType, getMissingRequiredColumns, COLUMN_CONFIGS } from './utils/columnMappingConfig';
import { validateImportFile } from './utils/importValidation';

const STEP_IDS: ImportStepId[] = ['file_type', 'upload', 'column_mapping', 'validation'];
const STEP_LABEL_KEYS: Record<ImportStepId, string> = {
  file_type: 'importWizard.steps.fileType',
  upload: 'importWizard.steps.upload',
  column_mapping: 'importWizard.steps.columnMapping',
  validation: 'importWizard.steps.validation',
};

const initialState: ImportWizardState = {
  currentStep: 1,
  files: [],
  activeFileIndex: 0,
  layerTargets: {},
};

function inferFileTypeFromColumns(columns: string[]): ImportFileType {
  const lower = columns.map(c => c.toLowerCase().trim());

  const fleetSignals = ['vehicle_id', 'vehicleid', 'shift_start', 'shiftstart', 'shift_end', 'shiftend', 'start_lat', 'end_lat', 'standard_seats'];
  const fleetHits = fleetSignals.filter(s => lower.some(c => c.includes(s))).length;
  if (fleetHits >= 3) return 'fleet_shifts';

  const rideSignals = ['origin_lat', 'originlat', 'dest_lat', 'destlat', 'from_lat', 'fromlat', 'to_lat', 'tolat', 'request_time', 'requesttime', 'pickup_lat', 'dropoff_lat', 'departure_lat', 'arrival_lat'];
  const rideHits = rideSignals.filter(s => lower.some(c => c.includes(s))).length;
  if (rideHits >= 2) return 'rides';

  const polygonSignals = ['wkt', 'polygon', 'geometry', 'geom', 'the_geom'];
  const polygonHits = polygonSignals.filter(s => lower.some(c => c === s)).length;
  if (polygonHits >= 1) return 'polygons';

  const pointSignals = ['latitude', 'lat', 'longitude', 'lng', 'lon', 'stop_lat', 'stop_lon'];
  const pointHits = pointSignals.filter(s => lower.some(c => c === s)).length;
  if (pointHits >= 2) return 'points';

  return 'rides';
}

function inferFileType(detectedType: string, columns: string[]): ImportFileType {
  if (detectedType === 'gtfs') return 'gtfs';
  if (columns.length > 0) return inferFileTypeFromColumns(columns);
  return 'rides';
}

export function useImportWizardState() {
  const [state, setState] = useState<ImportWizardState>(initialState);

  const currentStepId = STEP_IDS[state.currentStep - 1] ?? 'file_type';
  const totalSteps = STEP_IDS.length;

  const setStep = useCallback((step: number) => {
    setState(prev => ({
      ...prev,
      currentStep: Math.max(1, Math.min(step, STEP_IDS.length)),
    }));
  }, []);

  const nextStep = useCallback(() => {
    setState(prev => ({
      ...prev,
      currentStep: Math.min(prev.currentStep + 1, STEP_IDS.length),
    }));
  }, []);

  const prevStep = useCallback(() => {
    setState(prev => ({
      ...prev,
      currentStep: Math.max(prev.currentStep - 1, 1),
    }));
  }, []);

  const addFiles = useCallback(async (newFiles: File[], preferredType?: ImportFileType | null) => {
    const entries: ImportFileEntry[] = [];

    for (const file of newFiles) {
      const detectedType = detectFileType(file);
      const entry: ImportFileEntry = {
        id: uuidv4(),
        file,
        fileName: file.name,
        fileSize: file.size,
        detectedType,
        fileType: 'rides',
        parsedColumns: [],
        parsedRows: [],
        columnMapping: {},
        mappingStatus: detectedType === 'gtfs' || detectedType === 'kml' ? 'not-required' : 'pending',
        validationResult: null,
        parseError: null,
      };

      if (detectedType === 'excel' || detectedType === 'csv') {
        try {
          const parsed = detectedType === 'excel'
            ? await parseExcelFile(file)
            : await parseCsvFile(file);
          entry.parsedColumns = parsed.columns;
          entry.parsedRows = parsed.rows;
          // The type picked on step 1 wins; column sniffing is only the fallback.
          const fileType = preferredType && preferredType !== 'gtfs'
            ? preferredType
            : inferFileType(detectedType, parsed.columns);
          entry.fileType = fileType;
          entry.columnMapping = guessColumnMappingForType(parsed.columns, fileType);
          const hasAllRequired = getMissingRequiredColumns(fileType, entry.columnMapping).length === 0;
          entry.mappingStatus = hasAllRequired ? 'auto-mapped' : 'pending';
        } catch (err: any) {
          entry.parseError = err.message || 'Failed to parse file.';
        }
      } else if (detectedType === 'kml') {
        // ponytail: tag sniff, not a full parse; mixed files pick polygons if any exist
        const text = await file.text();
        entry.fileType = /<Polygon[\s>]/.test(text) ? 'polygons' : 'points';
      } else {
        entry.fileType = inferFileType(detectedType, []);
      }

      entries.push(entry);
    }

    setState(prev => ({
      ...prev,
      files: [...prev.files, ...entries],
    }));
  }, []);

  const removeFile = useCallback((fileId: string) => {
    setState(prev => {
      const files = prev.files.filter(f => f.id !== fileId);
      const { [fileId]: _, ...layerTargets } = prev.layerTargets;
      return {
        ...prev,
        files,
        layerTargets,
        activeFileIndex: Math.min(prev.activeFileIndex, Math.max(files.length - 1, 0)),
      };
    });
  }, []);

  const setFileType = useCallback((fileId: string, fileType: ImportFileType) => {
    setState(prev => ({
      ...prev,
      files: prev.files.map(f => {
        if (f.id !== fileId) return f;
        const newMapping = f.parsedColumns.length > 0
          ? guessColumnMappingForType(f.parsedColumns, fileType)
          : {};
        const hasAllRequired = f.parsedColumns.length > 0
          ? getMissingRequiredColumns(fileType, newMapping).length === 0
          : false;
        return {
          ...f,
          fileType,
          columnMapping: newMapping,
          mappingStatus: fileType === 'gtfs' || f.detectedType === 'kml' ? 'not-required' as const :
            hasAllRequired ? 'auto-mapped' as const : 'pending' as const,
          validationResult: null,
        };
      }),
    }));
  }, []);

  const setColumnMapping = useCallback((fileId: string, mapping: Record<string, string>) => {
    setState(prev => ({
      ...prev,
      files: prev.files.map(f => {
        if (f.id !== fileId) return f;
        return {
          ...f,
          columnMapping: mapping,
          mappingStatus: 'manually-mapped' as const,
          validationResult: null,
        };
      }),
    }));
  }, []);

  const setActiveFileIndex = useCallback((index: number) => {
    setState(prev => ({
      ...prev,
      activeFileIndex: Math.max(0, Math.min(index, prev.files.length - 1)),
    }));
  }, []);

  const setLayerTarget = useCallback((fileId: string, target: LayerTarget) => {
    setState(prev => ({
      ...prev,
      layerTargets: { ...prev.layerTargets, [fileId]: target },
    }));
  }, []);

  const runValidation = useCallback(() => {
    setState(prev => ({
      ...prev,
      files: prev.files.map(f => {
        if (f.mappingStatus === 'not-required' || f.parseError) return f;
        const result = validateImportFile(f.fileType, f.parsedRows, f.columnMapping);
        return { ...f, validationResult: result };
      }),
    }));
  }, []);

  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  const tabularFiles = useMemo(() =>
    state.files.filter(f => f.mappingStatus !== 'not-required' && !f.parseError),
    [state.files]
  );

  const canProceed = useMemo(() => {
    switch (currentStepId) {
      case 'file_type':
        return true;
      case 'upload':
        return state.files.length > 0 && state.files.every(f => !f.parseError);
      case 'column_mapping': {
        return tabularFiles.every(f => {
          const missing = getMissingRequiredColumns(f.fileType, f.columnMapping);
          return missing.length === 0;
        });
      }
      case 'validation':
        return state.files.every(f => {
          if (f.mappingStatus === 'not-required') return true;
          if (f.parseError) return false;
          // Bad rows are skipped on import, so only block when nothing would import
          return (f.validationResult?.validRowCount ?? 0) > 0;
        });
      default:
        return true;
    }
  }, [currentStepId, state.files, tabularFiles]);

  const steps: ImportStepDescriptor[] = useMemo(() =>
    STEP_IDS.map((id, index) => ({
      id,
      label: STEP_LABEL_KEYS[id],
      enabled: index + 1 <= state.currentStep,
      completed: index + 1 < state.currentStep,
    })),
    [state.currentStep]
  );

  return {
    state,
    steps,
    currentStepId,
    totalSteps,
    canProceed,
    setStep,
    nextStep,
    prevStep,
    addFiles,
    removeFile,
    setFileType,
    setColumnMapping,
    setActiveFileIndex,
    setLayerTarget,
    runValidation,
    reset,
  };
}
